import {
  DEFAULT_ROLE as DEFAULT_IMAGE_REFERENCE_ROLE,
  IMAGE_ROLES,
} from './imageRoles.js';
import {
  AUDIO_REFERENCE_ROLE_VALUES,
  IMAGE_REFERENCE_ROLE_VALUES,
  NATIVE_IMAGE_REFERENCE_ROLE_VALUES,
  VIDEO_REFERENCE_ROLE_VALUES,
} from './referenceRoleValues.js';

const SHARED_REFERENCE_ROLE_COLORS = Object.freeze({
  subject: 'var(--color-cyan-500)',
  style: 'var(--color-violet-500)',
  scene: 'var(--color-amber-500)',
  structure: 'var(--color-green-500)',
});

export {
  AUDIO_REFERENCE_ROLE_VALUES,
  DEFAULT_IMAGE_REFERENCE_ROLE,
  IMAGE_REFERENCE_ROLE_VALUES,
  NATIVE_IMAGE_REFERENCE_ROLE_VALUES,
  VIDEO_REFERENCE_ROLE_VALUES,
};

export const NATIVE_IMAGE_REFERENCE_ROLES = Object.freeze({
  // Deliberately uncoloured: these name a payload position, not a semantic
  // role, and the slot is chosen by array order rather than by the label.
  // Colour is reserved for roles the user can actually choose between.
  'native-first': Object.freeze({
    id: 'native-first',
    label: 'First',
    description: 'Use this image as the opening frame',
    roleColor: null,
  }),
  'native-last': Object.freeze({
    id: 'native-last',
    label: 'Last',
    description: 'Use this image as the ending frame',
    roleColor: null,
  }),
});

export const DEFAULT_VIDEO_REFERENCE_ROLE = 'motion';

export const VIDEO_REFERENCE_ROLES = Object.freeze({
  motion: Object.freeze({
    id: 'motion',
    ownsFraming: true,
    label: 'Motion',
    description: 'Follow the subject movement, action timing, and physical motion from this clip',
    roleColor: 'var(--color-orange-500)',
  }),
  camera: Object.freeze({
    id: 'camera',
    ownsFraming: true,
    label: 'Camera',
    description: 'Follow the camera movement, framing, and shot timing from this clip',
    roleColor: 'var(--color-pink-500)',
  }),
  subject: Object.freeze({
    id: 'subject',
    ownsFraming: false,
    label: IMAGE_ROLES.subject.label,
    description: 'Use the subject identity and appearance from this clip',
    roleColor: SHARED_REFERENCE_ROLE_COLORS.subject,
  }),
  style: Object.freeze({
    id: 'style',
    ownsFraming: false,
    label: IMAGE_ROLES.style.label,
    description: 'Use the visual style, color, lighting, and texture from this clip',
    roleColor: SHARED_REFERENCE_ROLE_COLORS.style,
  }),
  scene: Object.freeze({
    id: 'scene',
    ownsFraming: false,
    label: IMAGE_ROLES.scene.label,
    description: 'Use the environment and setting from this clip',
    roleColor: SHARED_REFERENCE_ROLE_COLORS.scene,
  }),
  structure: Object.freeze({
    id: 'structure',
    ownsFraming: false,
    label: IMAGE_ROLES.structure.label,
    description: 'Use the composition, blocking, framing, and spatial layout from this clip',
    roleColor: SHARED_REFERENCE_ROLE_COLORS.structure,
  }),
});

export const IMAGE_REFERENCE_ROLES = Object.freeze(
  {
    ...NATIVE_IMAGE_REFERENCE_ROLES,
    ...Object.fromEntries(IMAGE_REFERENCE_ROLE_VALUES.map((role) => [
      role,
      Object.freeze({
        id: role,
        label: IMAGE_ROLES[role].label,
        description: IMAGE_ROLES[role].description,
        roleColor: SHARED_REFERENCE_ROLE_COLORS[role],
      }),
    ])),
  },
);

export const AUDIO_REFERENCE_ROLES = Object.freeze({
  'audio-reference': Object.freeze({
    id: 'audio-reference',
    label: 'Audio',
    description: 'Use this clip as audio guidance',
    roleColor: 'var(--color-sky-500)',
  }),
});

export function getMediaReferenceRoleDefinitions(mediaType) {
  if (mediaType === 'video') return VIDEO_REFERENCE_ROLES;
  if (mediaType === 'audio') return AUDIO_REFERENCE_ROLES;
  return IMAGE_REFERENCE_ROLES;
}

export function getMediaReferenceRoleValues(mediaType) {
  if (mediaType === 'video') return VIDEO_REFERENCE_ROLE_VALUES;
  if (mediaType === 'audio') return AUDIO_REFERENCE_ROLE_VALUES;
  return IMAGE_REFERENCE_ROLE_VALUES;
}

export function normalizeMediaReferenceRole(mediaType, role) {
  const definitions = getMediaReferenceRoleDefinitions(mediaType);
  if (typeof role === 'string' && Object.hasOwn(definitions, role)) {
    return role;
  }
  if (mediaType === 'video') return DEFAULT_VIDEO_REFERENCE_ROLE;
  if (mediaType === 'audio') return AUDIO_REFERENCE_ROLE_VALUES[0];
  return DEFAULT_IMAGE_REFERENCE_ROLE;
}

export function getMediaReferenceRoleDefinition(mediaType, role) {
  const normalizedRole = normalizeMediaReferenceRole(mediaType, role);
  return getMediaReferenceRoleDefinitions(mediaType)[normalizedRole];
}

/**
 * Resolve the selectable roles for a media type.
 *
 * Fails CLOSED: a route that declares no roles offers none. An undeclared list
 * means "this transport cannot carry a role", never "any role is fine" — the
 * previous fall-back to the full vocabulary is what let every model advertise
 * roles its provider silently discarded.
 */
export function getMediaReferenceRoleOptions(mediaType, allowedRoles = null) {
  if (!Array.isArray(allowedRoles)) return [];

  const definitions = getMediaReferenceRoleDefinitions(mediaType);
  return allowedRoles
    .filter((role) => Object.hasOwn(definitions, role))
    .map((role) => definitions[role]);
}

export function isNativeImageReferenceRole(role) {
  return NATIVE_IMAGE_REFERENCE_ROLE_VALUES.includes(role);
}

export function getEffectiveMediaReferenceRole(mediaType, role, {
  referenceMode = false,
} = {}) {
  const normalizedRole = normalizeMediaReferenceRole(mediaType, role);
  if (
    mediaType === 'image'
    && referenceMode
    && isNativeImageReferenceRole(normalizedRole)
  ) {
    return DEFAULT_IMAGE_REFERENCE_ROLE;
  }
  return normalizedRole;
}
